import React, { useEffect, useRef, useState } from 'react';
import { isEqual, uniq, uniqWith } from 'lodash';

import CardLayout from '@magoosh/layouts/card';
import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import fetch from 'utilities/fetch';
import { toSentence } from 'utilities/formatting';

interface Props {
  answers: AnswerData[];
  onChange?: (updatedAnswers: AnswerData[]) => void;
  onFinished?: () => void;
}

const ERROR = 'error';
const FINISHED = 'finished';
const PROCESSING = 'processing';

const finishedScoreReportIdsIn = (answers: AnswerData[]) => (
  uniq(
    answers
      .filter((answer) => answer.scoreReport.status === FINISHED)
      .map((answer) => answer.scoreReport.id)
  )
);

export default (props: Props) => {
  const poller = useRef(null);
  const [answers, setAnswers] = useState<AnswerData[]>(props.answers);
  const scoreReports = uniqWith(answers.map((answer) => answer.scoreReport), isEqual);

  // Score report ids whose finish we have already reported, so onFinished fires once per score
  // report as each one lands rather than only once all of them have finished
  const finishedScoreReportIds = useRef<number[]>(finishedScoreReportIdsIn(props.answers));
  // The last props.answers value we synced local state from (see the prop sync effect below)
  const lastSyncedAnswers = useRef<AnswerData[]>(props.answers);
  // Read through a ref so the poll interval always calls the latest callback
  const onFinishedRef = useRef(props.onFinished);
  onFinishedRef.current = props.onFinished;
  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  const status = (reports = scoreReports) => {
    if (scoreReports.every((scoreReport) => scoreReport.status === FINISHED)) {
      return FINISHED;
    } else if (scoreReports.some((scoreReport) => scoreReport.status === ERROR)) {
      return ERROR;
    } else {
      return PROCESSING;
    }
  };

  useEffect(() => {
    // Parents often build a fresh answers array on every render. Only a change by value (e.g. the
    // page refetching after grading) should replace the polled local state, which runs ahead of
    // props while grading is in progress.
    if (isEqual(props.answers, lastSyncedAnswers.current)) return;

    lastSyncedAnswers.current = props.answers;
    finishedScoreReportIds.current = uniq([
      ...finishedScoreReportIds.current,
      ...finishedScoreReportIdsIn(props.answers)
    ]);
    setAnswers(props.answers);
  }, [props.answers]);

  useEffect(() => {
    if (props.onChange && !isEqual(answers, props.answers)) props.onChange(answers);

    // Keep polling while any score report is still in progress, even if another one has already
    // errored, so a request that finishes after a sibling's error still refreshes the page
    const anyInProgress = scoreReports.some((scoreReport) => (
      scoreReport.status !== FINISHED && scoreReport.status !== ERROR
    ));

    if (anyInProgress) {
      pollForStatusUpdates();
    } else {
      clearInterval(poller.current);
    }

    return () => clearInterval(poller.current)
  }, [answers]);

  const pollForStatusUpdates = () => {
    if (poller.current) clearInterval(poller.current);

    poller.current = setInterval(() => {
      const ids = scoreReports.map((scoreReport) => scoreReport.id)

      fetch(paths.api.aiGradingRequestsStatus(ids)).then((response) => {
        // A poll that was in flight when the card unmounted must not update state or refetch
        if (!isMounted.current) return;

        const updatedDataByAnswerId = {};

        // Look at the grading requests and map to an Object keyed by answer id for fast lookup
        response.gradingRequests.forEach((gradingRequest) => (
          gradingRequest.answers.forEach((answer) => (
            updatedDataByAnswerId[answer.id] = {
              gradingRequestGradeValue: answer.score,
              scoreReport: {
                regradable: gradingRequest.regradable,
                status: gradingRequest.status
              }
            }
          ))
        ));

        setAnswers((answers) => (
          answers.map((answer) => {
            const updatedData = updatedDataByAnswerId[answer.id];

            return {
              ...answer,
              gradingRequestGradeValue: updatedData.gradingRequestGradeValue,
              scoreReport: { ...answer.scoreReport, ...updatedData.scoreReport },
            };
          })
        ));

        // Report each newly finished grading request from the poll payload. Local state inside
        // this interval callback is a stale closure, so it can't be used to detect the change.
        const newlyFinishedIds = response.gradingRequests
          .filter((gradingRequest) => gradingRequest.status === FINISHED)
          .map((gradingRequest) => gradingRequest.id)
          .filter((id) => finishedScoreReportIds.current.indexOf(id) === -1);

        if (newlyFinishedIds.length) {
          finishedScoreReportIds.current = [...finishedScoreReportIds.current, ...newlyFinishedIds];
          if (onFinishedRef.current) onFinishedRef.current();
        }
      });
    }, 5000);
  };

  const regradableScoreReportIds = () => {
    return scoreReports.filter((scoreReport) => (
      scoreReport.status === ERROR && scoreReport.regradable
    )).map((scoreReport) => scoreReport.id);
  };

  const retryGrading = async () => {
    const idsToRetry = regradableScoreReportIds();

    await Promise.all(idsToRetry.map((id) => (
      fetch(paths.api.aiGradingRequestRegrade(id), { method: 'POST' })
    )));

    setAnswers((answers) => (
      answers.map((answer) => {
        if (idsToRetry.indexOf(answer.scoreReport.id) === -1) {
          return answer;
        } else {
          return {
            ...answer,
            scoreReport: {
              ...answer.scoreReport,
              status: 'processing'
            }
          };
        }
      })
    ));
  };

  if (status() === PROCESSING) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon className="fa-spin" icon="refresh" style={{ color: '#2A6EB6' }} />
          </div>

          <strong>
            We're grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers. This can take a minute or two.
          </strong>
        </div>
      </CardLayout.Card>
    );
  } else if (status() === ERROR && !!regradableScoreReportIds().length) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon icon="exclamation-triangle" />
          </div>

          <div>
            <strong>
              We ran into an issue grading your answers.
              {' '}<a onClick={retryGrading} style={{ color: '#2A6EB6' }}>Retry grading</a>
              {' '}and if you are still having trouble, contact us at
              {' '}<a href="mailto:help@magoosh.com" style={{ color: '#2A6EB6' }}>help@magoosh.com</a>.
            </strong>
          </div>
        </div>
      </CardLayout.Card>
    );
  } else if (status() === ERROR) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon icon="exclamation-triangle" />
          </div>

          <div>
            <strong>
              We ran into repeated issues grading your answer and are not able to retry grading again.
            </strong>
          </div>
        </div>
      </CardLayout.Card>
    );
  } else {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <SparkleIcon color="#2A6EB6" size={20} />
          </div>

          <strong>
            We've finished grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers!
          </strong>
        </div>
      </CardLayout.Card>
    );
  }
};
